import React, { useState, useContext, useEffect, useCallback } from "react";
import { NotificationManager } from "react-notifications";
import { MdEdit, MdDelete, MdSave } from "react-icons/md";

import Input from "@components/Input";
import Button from "@components/Button";

import UserContext from "@components/context/userContext";
import atService from "@services/at";

const AtLogin = ({ onLogin, selectedEmpresas }) => {
    const profile = useContext(UserContext);
    const [loaded, setLoaded] = useState(false);
    const [edit, setEdit] = useState(true);
    const [saving, setSaving] = useState(false);
    const [removing, setRemoving] = useState(false);
    const [login, setLogin] = useState({
        username: '',
        password: '',
    });

    // Verificar se o login está preenchido (ambos os campos devem ter valor)
    const isLoginComplete = login.username && login.password && login.username.trim() !== '' && login.password.trim() !== '';

    const loadFunction = useCallback(async () => {
        if (!loaded) {
            setLoaded(true);
            try {
                const idempresa = selectedEmpresas ? profile.idEmpresaSec : profile.idEmpresa;
                const loginResult = await atService.getLoginById(idempresa);
        
                // Só considera configurado se ambos username e password existirem
                if (loginResult.username && loginResult.password) {
                    onLogin(loginResult);
                    setEdit(false);
                } else if (loginResult.username) {
                    // Se só tem username, mantém em modo de edição para completar a password
                    setEdit(true);
                }

                setLogin(loginResult);
            } catch (error) {
                console.error(error);
            }
        }
    }, [loaded, selectedEmpresas, profile.idEmpresaSec, profile.idEmpresa, onLogin]);

    useEffect(() => {
        loadFunction();
    }, [loadFunction]);

    const editMode = () => {
        setEdit(true);
    };

    const saveData = async () => {
        setSaving(true);
        setEdit(false);
        try {
            const response = await atService.setLogin({
                idempresa: selectedEmpresas ? profile.idEmpresaSec : profile.idEmpresa,
                username: login.username,
                password: login.password,
            });
            
            // Validar resposta - a API retorna { success, message, error, environment, validation_status }
            if (response && response.success === true) {
                // Credenciais validadas com sucesso
                NotificationManager.success(
                    response.message || 'Credenciais validadas e guardadas com sucesso',
                    'Login AT',
                    3000
                );
                
                onLogin({
                    username: login.username,
                    password: login.password,
                    validated: true,
                    environment: response.environment
                });
            } else {
                // Erro na validação
                const errorMsg = response?.error || 'Erro ao validar credenciais';
                NotificationManager.error(errorMsg, 'Erro ao Validar Login AT', 5000);
                setEdit(true); // Voltar ao modo de edição em caso de erro
            }
        } catch (error) {
            console.error(error);
            const errorMsg = error?.response?.data?.error || 'Erro ao validar credenciais';
            NotificationManager.error(errorMsg, 'Erro ao Validar Login AT', 5000);
            setEdit(true); // Voltar ao modo de edição em caso de erro
        } finally {
            setSaving(false);
        }
    };

    const handleChange = (e) => {
        let value = e.target.value;
        let name = e.target.name;
        
        setLogin({
            ...login,
            [name]: value,
        });
    };

    const handleRemove = async () => {
        if (!window.confirm('Tem a certeza que deseja remover as credenciais da AT? Esta ação não pode ser desfeita.')) {
            return;
        }
        
        setRemoving(true);
        try {
            await atService.unsetLogin(
                selectedEmpresas ? profile.idEmpresaSec : profile.idEmpresa
            );
            
            // Limpar estado local
            setLogin({ username: '', password: '' });
            setEdit(true);
            onLogin(null);
            
            alert('Credenciais removidas com sucesso!');
        } catch (error) {
            console.error(error);
            alert('Erro ao remover credenciais. Por favor, tente novamente.');
        } finally {
            setRemoving(false);
        }
    };

    return (
        <div style={{ 
            width: '100%',
            padding: '12px'
        }}>
            <div style={{
                background: '#ffffff',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                border: '1px solid #e1e5e9',
                overflow: 'hidden'
            }}>
                {/* Header compacto */}
                <div style={{
                    background: isLoginComplete ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                    padding: '12px 16px',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        <span role="img" aria-label="ícone de cadeado">🔐</span>
                        <span style={{ fontSize: '16px', fontWeight: '600' }}>AT Login</span>
                        {isLoginComplete && <span role="img" aria-label="ícone de verificação" style={{ fontSize: '12px' }}>✓</span>}
                    </div>
                    
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: 'rgba(255, 255, 255, 0.2)',
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '500'
                    }}>
                        <div style={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            background: isLoginComplete ? '#10f981' : '#fbbf24',
                            animation: isLoginComplete ? 'pulse 2s infinite' : 'none'
                        }} />
                        {isLoginComplete ? 'Configurado' : 'Pendente'}
                    </div>
                </div>

                {/* Conteúdo compacto */}
                <div style={{ padding: '16px' }}>
                    <div style={{
                        display: 'grid',
                        gap: '12px'
                    }}>
                        {/* Estado do Login - só mostra se configurado */}
                        {!edit && isLoginComplete ? (
                            <div style={{
                                background: '#f0fdf4',
                                border: '1px solid #22c55e',
                                borderRadius: '8px',
                                padding: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '16px'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    flex: 1
                                }}>
                                    <div style={{ fontSize: '24px' }}>
                                        <span role="img" aria-label="ícone de sucesso">✅</span>
                                    </div>
                                    
                                    <div>
                                        <div style={{
                                            fontSize: '16px',
                                            fontWeight: '600',
                                            color: '#166534',
                                            marginBottom: '4px'
                                        }}>
                                            Login AT Configurado
                                        </div>
                                        
                                        {login.username && (
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                background: 'rgba(34, 197, 94, 0.15)',
                                                padding: '6px 10px',
                                                borderRadius: '16px',
                                                border: '1px solid rgba(34, 197, 94, 0.3)',
                                                color: '#166534',
                                                fontSize: '12px',
                                                fontWeight: '500',
                                                width: 'fit-content'
                                            }}>
                                                <span role="img" aria-label="ícone de utilizador">👤</span>
                                                <span style={{ fontWeight: '600' }}>{login.username}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <button
                                        type="button"
                                        onClick={editMode}
                                        style={{
                                            background: '#f3f4f6',
                                            color: '#374151',
                                            border: '1px solid #d1d5db',
                                            padding: '8px 16px',
                                            fontSize: '12px',
                                            borderRadius: '6px',
                                            whiteSpace: 'nowrap',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px'
                                        }}
                                    >
                                        <MdEdit size={16} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleRemove}
                                        disabled={removing}
                                        style={{
                                            background: '#fee2e2',
                                            color: '#991b1b',
                                            border: '1px solid #fecaca',
                                            padding: '8px 16px',
                                            fontSize: '12px',
                                            borderRadius: '6px',
                                            whiteSpace: 'nowrap',
                                            cursor: removing ? 'not-allowed' : 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            opacity: removing ? 0.6 : 1
                                        }}
                                    >
                                        {removing ? (
                                            <span className="spinner-border spinner-border-sm" style={{ width: '14px', height: '14px', borderWidth: '2px' }}></span>
                                        ) : (
                                            <MdDelete size={16} />
                                        )}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div style={{
                                background: '#f9fafb',
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                padding: '16px'
                            }}>
                                {/* Campos de edição */}
                                <div style={{ display: 'grid', gap: '12px', marginBottom: '16px' }}>
                                    <Input
                                        inputType="text"
                                        name="username"
                                        value={login.username}
                                        handleChange={handleChange}
                                        placeholder="Username"
                                    />
                                    <Input
                                        inputType="password"
                                        name="password"
                                        value={login.password}
                                        handleChange={handleChange}
                                        placeholder="Password"
                                    />
                                </div>
                                
                                {/* Botões de ação */}
                                <div style={{
                                    display: 'flex',
                                    gap: '8px',
                                    justifyContent: 'flex-end'
                                }}>
                                    {/* Só mostra botão Cancelar se já tiver login configurado */}
                                    {login.username && login.password && (
                                        <button
                                            type="button"
                                            onClick={() => setEdit(false)}
                                            style={{
                                                background: '#f3f4f6',
                                                color: '#374151',
                                                border: '1px solid #d1d5db',
                                                padding: '8px 16px',
                                                fontSize: '12px',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px'
                                            }}
                                        >
                                            <MdEdit size={16} />
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={saveData}
                                        disabled={saving || !isLoginComplete}
                                        style={{
                                            background: isLoginComplete ? '#3b82f6' : '#9ca3af',
                                            color: 'white',
                                            border: 'none',
                                            padding: '8px 16px',
                                            fontSize: '12px',
                                            borderRadius: '6px',
                                            opacity: isLoginComplete ? 1 : 0.6,
                                            cursor: (saving || !isLoginComplete) ? 'not-allowed' : 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px'
                                        }}
                                    >
                                        {saving ? (
                                            <span className="spinner-border spinner-border-sm" style={{ width: '14px', height: '14px', borderWidth: '2px' }}></span>
                                        ) : (
                                            <MdSave size={16} />
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Mensagem compacta */}
                        {!isLoginComplete && edit && (
                            <div style={{
                                background: '#fef3c7',
                                border: '1px solid #f59e0b',
                                borderRadius: '4px',
                                padding: '8px',
                                fontSize: '11px',
                                color: '#92400e',
                                textAlign: 'center'
                            }}>
                                <span role="img" aria-label="ícone de aviso">⚠️</span> Preencha ambos os campos
                            </div>
                        )}
                        
                    </div>
                </div>
            </div>

            {/* CSS para animação */}
            <style jsx>{`
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.5; }
                }
            `}</style>
        </div>
    );
};

export default AtLogin;
